import React, { useState, useEffect, useMemo, useRef } from 'react';
import { parseServerTime } from '@/lib/serverTime';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { FreeTrialWidget } from './FreeTrialWidget';
import { trackEvent } from '@/lib/analytics';
import { useSectionViewTracking } from '@/hooks/useSectionViewTracking';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Zap,
  Lock,
  BarChart3,
  Globe,
  Upload,
  Link as LinkIcon,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
  Sparkles,
  Shield,
  TrendingUp,
  Target,
  Settings,
  Code2,
  X,
  XCircle,
  Newspaper,
  Eye,
  Search,
  Activity,
  ClipboardCheck,
  ShoppingBag,
  Terminal,
  Puzzle,
} from 'lucide-react';
import { noticeApi, NoticeListItem, BannerNotice, NoticeDetail } from '@/api/noticeApi';
import NoticeBanner from '@/components/notice/NoticeBanner';
import { LoadingBlock } from '@/components/ui/spinner';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { useLanguage } from '@/contexts/LanguageContext';
import { landingCopy } from './landingCopy';

export function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  useSectionViewTracking(mainRef, 'landing_section_view');
  const { language } = useLanguage();
  const t = landingCopy[language];

  // 카페24 App URL 런치 파라미터(mall_id+hmac) 감지 - 등록된 App URL이 사이트 루트("/")라
  // 카페24 관리자에서 설치된 앱 아이콘을 클릭하면 이 파라미터들이 붙은 채로 여기(랜딩페이지)로
  // 리다이렉트된다. CAFE24_CMS_INTEGRATION_DESIGN.md §6 참고.
  // 2026-08-25 발견: 이 감지 로직이 없어서 /cafe24/launch(Cafe24Launch, hmac 검증 후
  // 세션 발급)가 전혀 호출되지 않고 그냥 랜딩페이지만 보여 로그인이 안 되던 버그
  // (실사용 재현: 앱 아이콘 클릭 → 그냥 메인 페이지만 표시됨).
  //
  // 2026-08-27 발견(카페24 앱스토어 심사 반려 "OAuth 인증실패" 조사): 카페24
  // 앱스토어에서 "설치" 버튼을 최초로 클릭하면(아직 OAuth 미완료) 같은 App URL로
  // mall_id만 붙고 hmac은 없는 상태로 온다 - 이 경우를 감지하는 분기가 없어서
  // 최초 설치 시도도 그냥 랜딩페이지만 보이고 OAuth 인가 화면으로 전혀 넘어가지
  // 않았다(설치 자체가 시작되지 않는 버그). mall_id만 있고 hmac이 없으면
  // /cafe24/install-landing으로 보내 §5.1 온보딩(로그인 확인 → OAuth 인가) 흐름을
  // 시작하도록 분기 추가.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mallId = params.get('mall_id');
    const hmac = params.get('hmac');
    if (mallId && hmac) {
      navigate({ pathname: '/cafe24/launch', search: location.search }, { replace: true });
    } else if (mallId && !hmac) {
      navigate({ pathname: '/cafe24/install-landing', search: location.search }, { replace: true });
    }
  }, [location.search, navigate]);

  // 다른 페이지에서 섹션 링크로 이동 시 해당 섹션으로 스크롤
  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string } | null)?.scrollTo;
    if (!scrollTo) return;
    const timer = setTimeout(() => {
      const element = document.getElementById(scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [location.state]);

  const [notices, setNotices] = useState<NoticeListItem[]>([]);
  const [noticeLoading, setNoticeLoading] = useState(true);
  const [noticeLayerOpen, setNoticeLayerOpen] = useState(false);
  const [selectedNoticeId, setSelectedNoticeId] = useState<number | null>(null);
  const [selectedNotice, setSelectedNotice] = useState<NoticeDetail | null>(null);
  const [selectedNoticeLoading, setSelectedNoticeLoading] = useState(false);
  const [selectedNoticeError, setSelectedNoticeError] = useState<string | null>(null);

  const parseNoticeItems = (payload: unknown): NoticeListItem[] => {
    if (Array.isArray(payload)) return payload as NoticeListItem[];
    if (payload && typeof payload === 'object') {
      const content = (payload as { content?: unknown }).content;
      if (Array.isArray(content)) return content as NoticeListItem[];
    }
    return [];
  };

  useEffect(() => {
    let mounted = true;

    const loadNotices = async () => {
      try {
        // 모든 페이지에서 공지사항을 로드 (페이징 처리)
        const res = await noticeApi.getList({ page: 0, size: 100 });
        const list = parseNoticeItems(res.data);
        
        if (!mounted) return;

        if (list.length > 0) {
          setNotices(list);
        } else {
          // 공지사항이 없으면 배너 공지만 로드
          const bannerRes = await noticeApi.getBanner();
          if (!mounted) return;
          const banners = Array.isArray(bannerRes.data) ? bannerRes.data : [];
          const fallback: NoticeListItem[] = banners.slice(0, 50).map((b: BannerNotice) => ({
            id: b.id,
            title: b.title,
            isPinned: false,
            isBanner: true,
            viewCount: 0,
            createdAt: b.createdAt,
          }));
          setNotices(fallback);
        }
      } catch (error) {
        // 에러 시에도 배너 공지는 시도하기
        try {
          const bannerRes = await noticeApi.getBanner();
          if (!mounted) return;
          const banners = Array.isArray(bannerRes.data) ? bannerRes.data : [];
          const fallback: NoticeListItem[] = banners.slice(0, 5).map((b: BannerNotice) => ({
            id: b.id,
            title: b.title,
            isPinned: false,
            isBanner: true,
            viewCount: 0,
            createdAt: b.createdAt,
          }));
          setNotices(fallback);
        } catch {
          if (mounted) {
            setNotices([]);
          }
        }
      } finally {
        if (mounted) setNoticeLoading(false);
      }
    };

    loadNotices();
    return () => {
      mounted = false;
    };
  }, []);

  const sortedNotices = useMemo(
    () => [...notices].sort((a, b) => {
      if (a.isBanner !== b.isBanner) return Number(b.isBanner) - Number(a.isBanner);
      if (a.isPinned !== b.isPinned) return Number(b.isPinned) - Number(a.isPinned);
      return b.createdAt.localeCompare(a.createdAt);
    }),
    [notices]
  );

  const headlineNotice = sortedNotices[0] ?? null;
  const showNoticeUi = !noticeLoading && sortedNotices.length > 0;

  const formatNoticeDate = (iso: string) =>
    parseServerTime(iso)?.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }) ?? '';

  const openNoticeLayer = (noticeId?: number) => {
    if (noticeId) {
      setSelectedNoticeId(noticeId);
    }
    setNoticeLayerOpen(true);
  };

  useEffect(() => {
    if (!noticeLayerOpen || sortedNotices.length === 0) return;
    setSelectedNoticeId((prev) => {
      if (prev && sortedNotices.some((notice) => notice.id === prev)) {
        return prev;
      }
      return sortedNotices[0].id;
    });
  }, [noticeLayerOpen, sortedNotices]);

  useEffect(() => {
    if (!noticeLayerOpen || !selectedNoticeId) return;

    let mounted = true;
    setSelectedNoticeLoading(true);
    setSelectedNoticeError(null);
    setSelectedNotice(null);

    noticeApi.getDetail(selectedNoticeId)
      .then((res) => {
        if (!mounted) return;
        setSelectedNotice(res.data);
      })
      .catch(() => {
        if (!mounted) return;
        setSelectedNoticeError(t.notice.loadError);
      })
      .finally(() => {
        if (mounted) setSelectedNoticeLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [noticeLayerOpen, selectedNoticeId]);

  return (
    <>
      <Helmet>
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href="https://alt.aigeneration-tech.com/" />
        <meta property="og:title" content={t.meta.title} />
        <meta property="og:description" content={t.meta.description} />
        <meta property="og:url" content="https://alt.aigeneration-tech.com/" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Header />
      
      <main ref={mainRef} className="flex-1">
        <NoticeBanner />

        {showNoticeUi && headlineNotice && (
          <section className="bg-slate-50 border-b border-slate-200">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto flex items-center gap-3 py-2.5">
                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                  <Newspaper className="w-3.5 h-3.5" /> {t.notice.badge}
                </span>

                <button
                  onClick={() => openNoticeLayer(headlineNotice.id)}
                  className="flex-1 text-left text-sm text-gray-700 hover:text-primary transition-colors truncate"
                >
                  {headlineNotice.title}
                </button>

                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={() => openNoticeLayer(headlineNotice.id)}
                >
                  {t.notice.viewAll}
                </Button>
              </div>
            </div>
          </section>
        )}

        {showNoticeUi && (
          <Dialog open={noticeLayerOpen} onOpenChange={setNoticeLayerOpen}>
            <DialogContent className="max-w-4xl p-0">
              <DialogHeader className="px-6 pt-6 pb-3 border-b">
                <DialogTitle className="flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-primary" />
                  {t.notice.dialogTitle}
                </DialogTitle>
                <DialogDescription>
                  {t.notice.dialogDesc}
                </DialogDescription>
              </DialogHeader>

              <div className="max-h-[75vh] overflow-y-auto">
                <div className="px-6 py-4 border-b bg-gray-50">
                  <div className="flex flex-wrap gap-2">
                    {sortedNotices.map((notice) => (
                      <button
                        key={notice.id}
                        className={`px-3 py-2 rounded-md border text-left transition-colors ${selectedNoticeId === notice.id ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white hover:bg-gray-100'}`}
                        onClick={() => setSelectedNoticeId(notice.id)}
                      >
                        <p className="text-sm text-gray-800 font-medium max-w-[18rem] truncate">{notice.title}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatNoticeDate(notice.createdAt)}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="px-6 py-5">
                  {selectedNoticeLoading ? (
                    <LoadingBlock size="md" py={10} iconClassName="text-gray-500" className="text-sm" label={t.notice.loadingBody} />
                  ) : selectedNoticeError ? (
                    <p className="text-center text-red-500 py-10">{selectedNoticeError}</p>
                  ) : selectedNotice ? (
                    <article className="space-y-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-400">{formatNoticeDate(selectedNotice.createdAt)}</span>
                        <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                          <Eye className="w-3.5 h-3.5" />
                          {(selectedNotice.viewCount ?? 0).toLocaleString()}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 break-words">{selectedNotice.title}</h3>
                      <div
                        className="notice-html text-gray-800"
                        style={{ minHeight: '220px' }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedNotice.content) }}
                      />
                    </article>
                  ) : (
                    <p className="text-center text-gray-400 py-10">{t.notice.noneSelected}</p>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t flex justify-end bg-gray-50">
                <Button size="sm" variant="outline" onClick={() => setNoticeLayerOpen(false)}>
                  {t.notice.close}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Hero Section */}
        <section data-ga-section="hero" className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 md:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4"
              >
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">{t.hero.badge}</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl md:text-6xl font-bold text-gray-900 mb-3"
              >
                {t.hero.titleLine1}<br />
                <span className="text-primary">{t.hero.titleLine2}</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-xl text-gray-600 mb-2 max-w-2xl mx-auto"
              >
                {t.hero.subtitle1}
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="text-base text-gray-500 mb-4 max-w-2xl mx-auto"
              >
                {t.hero.subtitle2a}<br />
                <span className="text-primary font-medium">{t.hero.subtitle2b}</span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center mb-6"
              >
                <Button
                  size="lg"
                  className="text-lg px-8 py-6"
                  onClick={() => { trackEvent('landing_cta_click', { cta: 'hero_start_free', target: '/signup' }); navigate('/signup'); }}
                >
                  {t.hero.ctaStart}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 border-green-500 text-green-700 hover:bg-green-50"
                  onClick={() => { trackEvent('landing_cta_click', { cta: 'hero_diagnose', target: '/diagnose' }); navigate('/diagnose'); }}
                >
                  <Search className="mr-2 h-5 w-5" />
                  {t.hero.ctaDiagnose}
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        <FreeTrialWidget />

        {/* ── Before/After 시각화 섹션 ── */}
        <motion.section
          data-ga-section="before_after"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20 bg-white"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {t.beforeAfter.heading}
                </h2>
                <p className="text-lg text-gray-600">
                  {t.beforeAfter.sub}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Before */}
                <div className="bg-red-50 rounded-xl p-6 border-2 border-red-200">
                  <div className="flex items-center gap-2 mb-4">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="font-semibold text-red-700">{t.beforeAfter.beforeLabel}</span>
                  </div>
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <code className="text-sm text-gray-700">
                      &lt;img src="product.jpg" alt=""&gt;
                    </code>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    {t.beforeAfter.beforeItems.map((text) => (
                      <div key={text} className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span>{text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* After */}
                <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-700">{t.beforeAfter.afterLabel}</span>
                  </div>
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <code className="text-sm text-gray-700">
                      &lt;img src="product.jpg" alt="{t.beforeAfter.afterCode}"&gt;
                    </code>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    {t.beforeAfter.afterItems.map((text) => (
                      <div key={text} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span>{text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── 무료 진단 프로모 섹션 ── */}
        <motion.section
          data-ga-section="diagnose_promo"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-16 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border-y border-green-100"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                {/* 왼쪽: 설명 */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                    <Shield className="w-4 h-4" />
                    {t.diagnosePromo.badge}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                    {t.diagnosePromo.headingLine1}<br />
                    <span className="text-green-600">{t.diagnosePromo.headingHighlight}</span> {t.diagnosePromo.headingLine2}<br />
                    {t.diagnosePromo.headingLine3}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {t.diagnosePromo.desc1}<strong>{t.diagnosePromo.descStrong1}</strong>{t.diagnosePromo.desc2}
                    <strong> {t.diagnosePromo.descStrong2}</strong>{t.diagnosePromo.desc3}
                  </p>

                  <div className="space-y-3 mb-8">
                    {[
                      { icon: ClipboardCheck, color: 'text-blue-600 bg-blue-50' },
                      { icon: TrendingUp,     color: 'text-green-600 bg-green-50' },
                      { icon: Zap,            color: 'text-purple-600 bg-purple-50' },
                      { icon: Activity,       color: 'text-orange-600 bg-orange-50' },
                    ].map(({ icon: Icon, color }, i) => (
                      <div key={t.diagnosePromo.features[i]} className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className="text-gray-700 text-sm">{t.diagnosePromo.features[i]}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base gap-2"
                      onClick={() => { trackEvent('landing_cta_click', { cta: 'diagnose_promo_start', target: '/diagnose' }); navigate('/diagnose'); }}
                    >
                      <Search className="w-5 h-5" />
                      {t.diagnosePromo.ctaStart}
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                    <p className="text-xs text-gray-400 self-center">
                      {t.diagnosePromo.ctaNote}
                    </p>
                  </div>
                </motion.div>

                {/* 오른쪽: 결과 미리보기 Mock-up */}
                <div className="bg-white rounded-2xl shadow-xl border border-green-100 overflow-hidden">
                  {/* 상단 URL 바 */}
                  <div className="bg-gray-50 border-b px-5 py-3 flex items-center gap-3">
                    <Globe className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-500 truncate">{t.diagnosePromo.mockUrl}</span>
                    <span className="ml-auto shrink-0 text-xs bg-green-100 text-green-700 font-medium px-2.5 py-1 rounded-full">{t.diagnosePromo.mockDone}</span>
                  </div>

                  {/* 점수 */}
                  <div className="grid grid-cols-2 divide-x border-b">
                    {[
                      { score: 62, color: '#f97316' },
                      { score: 78, color: '#22c55e' },
                    ].map(({ score, color }, i) => (
                      <div key={t.diagnosePromo.scoreLabels[i]} className="py-5 text-center">
                        <p className="text-3xl font-bold" style={{ color }}>{score}</p>
                        <p className="text-xs text-gray-400 mt-1">{t.diagnosePromo.scoreLabels[i]}</p>
                      </div>
                    ))}
                  </div>

                  {/* 이슈 목록 */}
                  <div className="p-5 space-y-2.5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t.diagnosePromo.issuesLabel}</p>
                    {t.diagnosePromo.issues.map(({ severity, text, impact }) => (
                      <div key={text} className="flex items-center gap-2.5 py-2 border-b border-gray-50 last:border-0">
                        <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                          severity === 'error'   ? 'bg-red-100 text-red-700' :
                          severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                                   'bg-blue-100 text-blue-700'
                        }`}>
                          {severity === 'error' ? t.diagnosePromo.severityLabels.error : severity === 'warning' ? t.diagnosePromo.severityLabels.warning : t.diagnosePromo.severityLabels.info}
                        </span>
                        <span className="text-xs text-gray-700 flex-1">{text}</span>
                        <span className="text-xs text-red-500 font-medium shrink-0">{impact}</span>
                      </div>
                    ))}
                  </div>

                  {/* ALT 자동생성 CTA */}
                  <div className="bg-primary/5 border-t px-5 py-3 flex items-center justify-between">
                    <span className="text-sm text-primary font-medium">{t.diagnosePromo.mockCta1}</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> {t.diagnosePromo.mockCta2}
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </motion.section>

        {/* Features Section */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          id="features"
          data-ga-section="features"
          className="py-20 bg-white"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {[Globe, LinkIcon, Upload].map((Icon, i) => (
                <Card key={t.features.cards1[i].title} className="border-2 hover:border-primary transition-colors">
                  <CardHeader>
                    <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle>{t.features.cards1[i].title}</CardTitle>
                    <CardDescription>
                      {t.features.cards1[i].desc}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[Zap, Lock, Sparkles].map((Icon, i) => (
                <Card key={t.features.cards2[i].title}>
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className="h-8 w-8 text-primary" />
                      <CardTitle>{t.features.cards2[i].title}</CardTitle>
                    </div>
                    <CardDescription>
                      {t.features.cards2[i].desc}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </motion.section>

        {/* ── 4가지 연동 방식 상세 소개 섹션 ── */}
        <motion.section
          data-ga-section="integration_methods"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20 bg-white"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-14">
                <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-primary text-sm font-medium">{t.integrationMethods.badge}</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {t.integrationMethods.heading}
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  {t.integrationMethods.sub}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  {
                    icon: Terminal,
                    accent: 'from-blue-600 to-cyan-500',
                    badgeColor: 'text-blue-700 bg-blue-100',
                    preview: (
                      <div className="rounded-lg bg-slate-900 text-slate-100 text-xs font-mono p-4 overflow-x-auto">
                        <div className="flex gap-1.5 mb-3">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                          <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                        </div>
                        <p><span className="text-purple-400">POST</span> <span className="text-cyan-300">/api/v1/alt-text</span></p>
                        <p className="text-slate-500">Authorization: Bearer &lt;API_KEY&gt;</p>
                        <p className="text-slate-500">{'{ "imageUrl": "..." }'}</p>
                        <p className="mt-2 text-green-400">→ 200 OK · {'{ "altText": "..." }'}</p>
                      </div>
                    ),
                    path: '/dashboard/api-docs',
                  },
                  {
                    icon: Code2,
                    accent: 'from-emerald-600 to-green-500',
                    badgeColor: 'text-emerald-700 bg-emerald-100',
                    preview: (
                      <div className="rounded-lg bg-slate-900 text-slate-100 text-xs font-mono p-4 overflow-x-auto">
                        <div className="flex gap-1.5 mb-3">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                          <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                        </div>
                        <p className="text-slate-500">&lt;!-- {t.integrationMethods.codeComment} --&gt;</p>
                        <p><span className="text-purple-400">&lt;script</span> <span className="text-cyan-300">src</span>=<span className="text-orange-300">&quot;snippet.js&quot;</span> <span className="text-cyan-300">data-key</span>=<span className="text-orange-300">&quot;...&quot;</span><span className="text-purple-400">&gt;&lt;/script&gt;</span></p>
                        <p className="mt-2 text-green-400">→ {t.integrationMethods.codeResult}</p>
                      </div>
                    ),
                    path: '/dashboard/snippet-guide',
                  },
                  {
                    icon: Puzzle,
                    accent: 'from-purple-600 to-indigo-500',
                    badgeColor: 'text-purple-700 bg-purple-100',
                    preview: (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-center gap-2 bg-white rounded-md border border-gray-200 px-3 py-2 mb-3 shadow-sm">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 rounded-full bg-gray-300" />
                            <span className="w-2 h-2 rounded-full bg-gray-300" />
                          </div>
                          <div className="flex-1 h-2 bg-gray-100 rounded-full" />
                          <Puzzle className="h-3.5 w-3.5 text-purple-500" />
                        </div>
                        <div className="inline-flex items-center gap-2 bg-purple-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md shadow-sm">
                          <Sparkles className="h-3.5 w-3.5" /> {t.integrationMethods.cards[2].previewLabel}
                        </div>
                        <p className="text-xs text-gray-500 mt-3">{t.integrationMethods.cards[2].previewNote}</p>
                      </div>
                    ),
                    path: '/dashboard/extension-guide',
                  },
                  {
                    icon: ShoppingBag,
                    accent: 'from-orange-600 to-amber-500',
                    badgeColor: 'text-orange-700 bg-orange-100',
                    preview: (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          {['Cafe24', '고도몰', '아임웹', 'Make#', '플레이오토', 'Shopify', '삽링커', '스마트스토어'].map((name) => (
                            <div key={name} className="bg-white border border-gray-200 rounded-md py-1.5 text-center text-[11px] font-medium text-gray-600 shadow-sm">
                              {name}
                            </div>
                          ))}
                        </div>
                        <div className="inline-flex items-center gap-1.5 bg-orange-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md shadow-sm">
                          <CheckCircle2 className="h-3.5 w-3.5" /> {t.integrationMethods.cards[3].previewNote}
                        </div>
                      </div>
                    ),
                    path: '/dashboard/shop-extension-guide',
                  },
                ].map(({ icon: Icon, accent, badgeColor, preview, path }, i) => {
                  const card = t.integrationMethods.cards[i];
                  return (
                  <button
                    key={card.title}
                    onClick={() => { trackEvent('landing_cta_click', { cta: 'integration_card', target: path }); navigate(path); }}
                    className="group text-left bg-white border-2 border-gray-100 rounded-2xl p-6 md:p-8 hover:border-transparent hover:shadow-2xl transition-all duration-300 relative overflow-hidden"
                  >
                    <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${accent} scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`} />

                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br ${accent} shadow-md group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="h-7 w-7 text-white" />
                      </div>
                      <span className="text-[11px] font-bold text-gray-400 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
                        {card.stat}
                      </span>
                    </div>

                    <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full mb-3 ${badgeColor}`}>
                      {card.badge}
                    </span>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{card.headline}</p>

                    <ul className="space-y-2 mb-5">
                      {card.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mb-5">{preview}</div>

                    <div className="inline-flex items-center gap-1.5 font-semibold text-primary">
                      {card.cta}
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1.5 transition-transform duration-300" />
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── 일반 AI 비교 섹션 ── */}
        <section data-ga-section="ai_comparison" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-full mb-4">
                <span className="text-orange-600 text-sm font-medium">{t.aiComparison.badge}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                &ldquo;{t.aiComparison.heading}&rdquo;
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {t.aiComparison.sub}<strong>{t.aiComparison.subStrong}</strong>{t.aiComparison.subEnd}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
              {/* AI 단독 */}
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-xl">🤖</div>
                  <h3 className="text-xl font-bold text-gray-600">{t.aiComparison.leftTitle}</h3>
                </div>
                <ul className="space-y-4">
                  {t.aiComparison.leftItems.map((text, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <X className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-500">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* ALT Generation */}
              <div className="bg-gradient-to-br from-primary/5 to-blue-50 border-2 border-primary rounded-2xl p-8 relative overflow-hidden">
                <div className="absolute top-4 right-4">
                  <span className="bg-primary text-white text-xs px-3 py-1 rounded-full font-medium">{t.aiComparison.rightBadge}</span>
                </div>
                <div className="flex items-center gap-3 mb-6">
                  <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-10 h-10" />
                  <h3 className="text-xl font-bold text-primary">{t.aiComparison.rightTitle}</h3>
                </div>
                <ul className="space-y-4">
                  {t.aiComparison.rightItems.map((text, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-medium">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="max-w-5xl mx-auto bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <p className="text-amber-800 font-medium text-lg">
                {t.aiComparison.bottomNote1}<strong>{t.aiComparison.bottomStrong1}</strong>{t.aiComparison.bottomNote2}<strong>{t.aiComparison.bottomStrong2}</strong>{t.aiComparison.bottomNote3}
              </p>
            </div>
          </div>
        </section>

        {/* ── 컨텍스트 프로필 스포트라이트 ── */}
        <section data-ga-section="context_profile" className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full mb-6">
                    <Target className="h-4 w-4 text-purple-600" />
                    <span className="text-purple-700 text-sm font-medium">{t.contextProfile.badge}</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    &ldquo;{t.contextProfile.headingLine1}&rdquo;<br />
                    <span className="text-purple-600">{t.contextProfile.headingHighlight}</span>
                  </h2>
                  <p className="text-lg text-gray-600 mb-8">
                    {t.contextProfile.desc1}<strong>{t.contextProfile.descStrong1}</strong>
                    {t.contextProfile.desc2}<strong>{t.contextProfile.descStrong2}</strong>
                    {t.contextProfile.desc3}<strong>{t.contextProfile.descStrong3}</strong>{t.contextProfile.desc4}
                  </p>
                  <div className="space-y-4">
                    {t.contextProfile.items.map((item, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl">
                        <span className="text-2xl">{item.icon}</span>
                        <div>
                          <p className="font-semibold text-gray-800">{item.title}</p>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 프로필 카드 Mock-up */}
                <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-8 text-white shadow-2xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <Settings className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-bold">{t.contextProfile.mockTitle}</p>
                      <p className="text-purple-200 text-sm">{t.contextProfile.mockSubtitle}</p>
                    </div>
                  </div>
                  <div className="space-y-3 mb-6">
                    {t.contextProfile.mockRows.map((row, i) => (
                      <div key={i} className="flex justify-between items-center py-2 border-b border-white/10">
                        <span className="text-purple-200 text-sm">{row.label}</span>
                        <span className="text-white text-sm font-medium">{row.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-xs text-purple-200 mb-2">{t.contextProfile.exampleLabel}</p>
                    <p className="text-sm font-medium leading-relaxed">{t.contextProfile.exampleText}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section data-ga-section="roi" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    {t.benefits.headingLine1}<br /><span className="text-primary">{t.benefits.headingLine2}</span>
                  </h2>
                  <div className="space-y-6">
                    {[Shield, TrendingUp, Sparkles, Target].map((Icon, i) => (
                      <div key={t.benefits.items[i].title} className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className={`p-3 rounded-lg ${['bg-green-100', 'bg-blue-100', 'bg-purple-100', 'bg-orange-100'][i]}`}>
                            <Icon className={`h-6 w-6 ${['text-green-600', 'text-blue-600', 'text-purple-600', 'text-orange-600'][i]}`} />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold mb-2">{t.benefits.items[i].title}</h3>
                          <p className="text-gray-600">
                            {t.benefits.items[i].desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
              </div>

              <div className="bg-gradient-to-br from-primary to-blue-700 p-8 rounded-2xl text-white">
                <div className="space-y-6">
                  <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                    <div className="flex items-start justify-between mb-1">
                      <div className="text-4xl font-bold">{t.benefits.stats[0].value}</div>
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-blue-100 mt-1">{t.benefits.stats[0].badge}</span>
                    </div>
                    <div className="text-blue-100">{t.benefits.stats[0].label}</div>
                  </div>
                  <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                    <div className="flex items-start justify-between mb-1">
                      <div className="text-4xl font-bold">{t.benefits.stats[1].value}</div>
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-blue-100 mt-1">{t.benefits.stats[1].badge}</span>
                    </div>
                    <div className="text-blue-100">{t.benefits.stats[1].label}</div>
                  </div>
                  <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                    <div className="text-4xl font-bold mb-2">{t.benefits.stats[2].value}</div>
                    <div className="text-blue-100">{t.benefits.stats[2].label}</div>
                  </div>
                </div>
                <p className="text-xs text-blue-200/60 mt-4 text-right">{t.benefits.statsNote}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 사용자 유형별 활용 사례 ── */}
        <section data-ga-section="use_cases" className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t.useCases.heading}
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {t.useCases.sub}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                { bg: 'bg-orange-100' },
                { bg: 'bg-blue-100' },
                { bg: 'bg-green-100' },
              ].map(({ bg }, i) => {
                const card = t.useCases.cards[i];
                return (
                  <Card key={card.title} className="border-2 hover:border-primary hover:shadow-lg transition-all">
                    <CardHeader>
                      <div className={`${bg} w-14 h-14 rounded-xl flex items-center justify-center mb-4 text-3xl`}>
                        {card.emoji}
                      </div>
                      <CardTitle className="text-xl">{card.title}</CardTitle>
                      <CardDescription className="text-base leading-relaxed">
                        {card.desc}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm text-gray-600">
                        {card.bullets.map((bullet) => (
                          <li key={bullet} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />{bullet}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" data-ga-section="pricing" className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t.pricing.heading}
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {t.pricing.sub}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {t.pricing.plans.map((plan) => (
                <Card key={plan.id} className={plan.popular ? 'border-2 border-primary shadow-lg relative' : 'border-2'}>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                        {t.pricing.popularBadge}
                      </span>
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      {!plan.contact && <span className="text-gray-600">{t.pricing.perMonth}</span>}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={plan.popular ? 'default' : 'outline'}
                      className="w-full"
                      onClick={plan.contact ? undefined : () => { trackEvent('landing_cta_click', { cta: `pricing_${plan.id}_start`, target: '/signup' }); navigate('/signup'); }}
                    >
                      {plan.contact ? t.pricing.contactButton : t.pricing.startButton}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" data-ga-section="about" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t.about.heading}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {t.about.p1}
              </p>
              <p className="text-lg text-gray-600 mb-8">
                {t.about.p2}
              </p>
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <h3 className="text-xl font-semibold mb-4">{t.about.infoHeading}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div>
                    <p className="text-sm text-gray-600">{t.about.labels.name}</p>
                    <p className="font-semibold">{t.about.values.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{t.about.labels.ceo}</p>
                    <p className="font-semibold">{t.about.values.ceo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{t.about.labels.bizNum}</p>
                    <p className="font-semibold">{t.about.values.bizNum}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{t.about.labels.mailOrderNum}</p>
                    <p className="font-semibold">{t.about.values.mailOrderNum}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{t.about.labels.founded}</p>
                    <p className="font-semibold">{t.about.values.founded}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section data-ga-section="bottom_cta" className="py-20 bg-gradient-to-br from-primary to-blue-700 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t.cta.heading}
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              {t.cta.desc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="text-lg px-8 py-6"
                onClick={() => { trackEvent('landing_cta_click', { cta: 'bottom_cta_start_free', target: '/signup' }); navigate('/signup'); }}
              >
                {t.cta.ctaStart}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 bg-white/10 hover:bg-white/20 text-white border-white/30"
                onClick={() => { trackEvent('landing_cta_click', { cta: 'bottom_cta_contact', target: 'mailto' }); window.location.href = 'mailto:alt.generation@outlook.com'; }}
              >
                {t.cta.ctaContact}
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
    </>
  );
}
